import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

export interface ReviewSubmissionData {
  title: string;
  content: string;
}

interface ReviewSubmissionFormProps {
  promptId: number;
  onSubmit: (data: ReviewSubmissionData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  initialData?: Partial<ReviewSubmissionData>;
}

export const ReviewSubmissionForm: React.FC<ReviewSubmissionFormProps> = ({
  promptId,
  onSubmit,
  onCancel,
  isLoading = false,
  initialData
}) => {
  const [formData, setFormData] = useState<ReviewSubmissionData>({
    title: initialData?.title || '',
    content: initialData?.content || ''
  });

  const [errors, setErrors] = useState<{
    title?: string;
    content?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Review title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Review content is required';
    } else if (formData.content.length < 10) {
      newErrors.content = 'Content must be at least 10 characters';
    } else if (formData.content.length > 2000) {
      newErrors.content = 'Content must be 2000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on successful submission
      setFormData({
        title: '',
        content: ''
      });
    } catch (error) {
      // Error handling is done by parent component
      console.error('Error submitting review:', error);
    }
  };

  const handleInputChange = (
    field: keyof Pick<ReviewSubmissionData, 'title' | 'content'>,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error on input
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Write a Review</CardTitle>
        <p className="text-sm text-gray-600">
          Share your experience with this prompt to help other users.
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="review-title" className="block text-sm font-medium text-gray-700 mb-2">
              Review Title *
            </label>
            <Input
              id="review-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Summarize your experience..."
              maxLength={100}
              disabled={isLoading}
              className={errors.title ? 'border-red-500' : ''}
            />
            <div className="flex justify-between mt-1">
              {errors.title && (
                <p className="text-red-600 text-sm">{errors.title}</p>
              )}
              <p className="text-gray-500 text-xs ml-auto">
                {formData.title.length}/100 characters
              </p>
            </div>
          </div>

          {/* Content */}
          <div>
            <label htmlFor="review-content" className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Review *
            </label>
            <Textarea
              id="review-content"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Share your detailed thoughts about this prompt. What worked well? How did you use it? Any suggestions for improvement?"
              maxLength={2000}
              rows={6}
              disabled={isLoading}
              className={errors.content ? 'border-red-500' : ''}
            />
            <div className="flex justify-between mt-1">
              {errors.content && (
                <p className="text-red-600 text-sm">{errors.content}</p>
              )}
              <p className="text-gray-500 text-xs ml-auto">
                {formData.content.length}/2000 characters
              </p>
            </div>
          </div>

          {/* Guidelines */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Review Guidelines</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Be specific about how you used the prompt</li>
              <li>• Mention what worked well and what could be improved</li>
              <li>• Keep feedback constructive and respectful</li>
              <li>• Avoid personal information or inappropriate content</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={isLoading || !formData.title.trim() || !formData.content.trim()}
              className="min-w-[120px]"
            >
              {isLoading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewSubmissionForm;